import { formatCents, type PricingConfig } from '@lbhl/shared';
import { parseSegments, sumOf, type Tier } from '../lib/tiers.js';
import { SegmentRows, type Segment } from './SegmentRows.jsx';

interface Props {
  pricing: PricingConfig;
  tiers: Tier[];
  segments: Record<string, Segment[]>;
  onChange(surfaceCode: string, segments: Segment[]): void;
}

/**
 * Measure once, price every package.
 *
 * A heading per tier, and under each one only the surfaces that tier ADDS. The
 * customer receives all three prices and chooses afterwards, so there is nothing to
 * select here — Signature's footage is Essential's plus what is entered under
 * Signature, and Showcase's is that plus its own.
 */
export function Measurements({ pricing, tiers, segments, onChange }: Props) {
  return (
    <section className="card">
      <h2>Measurements</h2>
      <div className="note">
        Each heading adds to the one above it. Enter a roofline in as many pieces as
        you measured it — press <strong>+</strong> for another box and they are added
        up for you.
      </div>

      {tiers.map(({ pkg, introduces }) => (
        <div className="tier" key={pkg.code}>
          <h3>
            {pkg.name}
            <span className="desc">{pkg.description}</span>
          </h3>

          {introduces.length === 0 ? (
            <div className="note dim">
              Nothing extra to measure — {pkg.name} is priced on everything above.
            </div>
          ) : (
            introduces.map((surface) => {
              const rows = segments[surface.code] ?? [];
              const total = sumOf(parseSegments(rows, surface.isAccent));
              return (
                <SegmentRows
                  key={surface.code}
                  label={surface.name}
                  unit={surface.isAccent ? 'strands' : 'ft'}
                  hint={
                    surface.isAccent
                      ? `mini-light strands @ ${formatCents(pricing.pricePerStrandCents)} each`
                      : 'linear feet'
                  }
                  segments={rows}
                  total={total}
                  onChange={(next) => onChange(surface.code, next)}
                />
              );
            })
          )}
        </div>
      ))}
    </section>
  );
}
